import React, { useContext } from 'react'
import { Radio, RadioGroup, makeStyles } from '@material-ui/core'
import type { NotificationTypeConfig } from 'paintscout'
import UserContext from '../../providers/UserProvider/UserContext'
import { NewFormSection as FormSection } from '../../components/FormSection'
import FormSectionTitle from '../../components/FormSectionTitle'
import { getFeature, getObjectLabels } from '@paintscout/util/builder'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import { connect, useFormikContext, Form } from 'formik'
import type { ProfileFormikValues } from '../ProfileFormik'
import FormControlLabel from '../../components/FormControlLabel'
import type { WithUseStyles } from '../../styles'
import { getHighestRole } from '@paintscout/util/users'
import Checkbox from '../../components/Checkbox'
import SettingsPage from '../../components/SettingsPage'
import Typography from '../../components/Typography'
import Divider from '../../components/Divider'
import { useUser } from '../../providers/UserProvider'

export interface NotificationsFormProps extends WithUseStyles<typeof useStyles> {
  showAdmin?: boolean
  hasCRM?: boolean
}

// keep this in sync with views/notification-subscribers.ts
// (ideally we keep this in a @paintscout/util function, but we can't use @paintscout/util functions in couchdb views)
// so it's best to be explicit both here and in the view
const defaultNotificationTypes: Record<string, NotificationTypeConfig> = {
  'quote-accepted': {
    enabled: false,
    triggeredBy: 'self'
  },
  'quote-declined': {
    enabled: false,
    triggeredBy: 'self'
  },
  'quote-viewed': {
    enabled: false,
    triggeredBy: 'self',
    frequency: 'firstView'
  },
  'invoice-viewed': {
    enabled: false,
    triggeredBy: 'self',
    frequency: 'firstView'
  },
  'quote-additional-work-accepted': {
    enabled: false,
    triggeredBy: 'self'
  },
  'quote-bounced': {
    enabled: false,
    triggeredBy: 'self'
  },
  'invoice-bounced': {
    enabled: false,
    triggeredBy: 'self'
  },
  'quote-message': {
    enabled: false,
    triggeredBy: 'self'
  },
  'quote-assigned': {
    enabled: true,
    triggeredBy: 'self'
  },
  'stats-digest-company': {
    enabled: false,
    triggeredBy: 'self'
  },
  'stats-digest-own': {
    enabled: false,
    triggeredBy: 'self'
  },
  'integration-quote-error': {
    enabled: false,
    triggeredBy: 'self'
  },
  'integration-contact-error': {
    enabled: false,
    triggeredBy: 'self'
  },
  'integration-authentication-error': {
    enabled: false,
    triggeredBy: 'self'
  },
  'payment-received': {
    enabled: false,
    triggeredBy: 'self'
  },
  'deal-assigned': {
    enabled: false,
    triggeredBy: 'self'
  },
  'lead-form-submitted': {
    enabled: false,
    triggeredBy: 'self'
  },
  'event-assigned': {
    enabled: false,
    triggeredBy: 'self'
  },
  'event-reassigned': {
    enabled: false,
    triggeredBy: 'self'
  },
  'event-updated': {
    enabled: false,
    triggeredBy: 'self'
  },
  'event-cancelled': {
    enabled: false,
    triggeredBy: 'self'
  },
  'task-assigned': {
    enabled: false,
    triggeredBy: 'self'
  },
  'task-upcoming-weekly': {
    enabled: false,
    triggeredBy: 'self'
  },
  'task-overdue-daily': {
    enabled: false,
    triggeredBy: 'self'
  }
}

const useStyles = makeStyles((theme) => ({
  section: {},
  option: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginBottom: theme.spacing(2)
    }
  },
  formSectionTitle: {
    paddingTop: theme.spacing(2)
  },
  formSection: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  },
  divider: {
    padding: `${theme.spacing()}px 0 ${theme.spacing(2)}px 0`
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexDirection: 'column'
    }
  },
  checkboxLabel: {
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      '& .MuiFormControlLabel-label': {
        lineHeight: 1.5
      }
    }
  },
  taskOption: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3) // 24px spacing between all task options
    }
  },
  radio: {
    width: 160
  },
  dropdown: {
    margin: theme.spacing(2, 0),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing()
    }
  },
  filled: {
    padding: theme.spacing(2, 2),
    width: 200
  },
  subheaderText: {
    padding: theme.spacing(1, 0)
  },
  subheaderDivider: {
    marginBottom: theme.spacing(2)
  },
  mobileLabelDiv: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    marginLeft: theme.spacing(3)
  }
}))

function NotificationsForm(props: NotificationsFormProps) {
  const classes = useStyles(props)
  const { showAdmin, hasCRM } = props
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { values } = useFormikContext<ProfileFormikValues>()
  const { isViewer, isAdmin, isCrew } = useUser()
  const highestRole = getHighestRole(values?.user?.app_metadata?.roles)
  const featureStatsDigest = getFeature({ options, path: 'notifications.statsDigest' })
  const featureQuoteViewed = getFeature({ options, path: 'notifications.quoteViewed' })
  const featureNotifications = getFeature({ options, path: 'notifications.enabled' })
  console.log({ isViewer, isAdmin, isCrew, highestRole })
  const showSalesAdminFeature = (feature: boolean) =>
    featureNotifications && feature && ['superadmin', 'admin', 'sales'].includes(highestRole)
  return (
    <SettingsPage
      title={`Notifications`}
      callout={{
        path: 'profile-notifications',
        content: `Customize your email notification preferences.`,
        learnMoreLink: 'http://help.paintscout.com/en/articles/5509580-setting-up-email-notifications'
      }}
    >
      <Form>
        <FormSection>
          <FormSectionTitle
            title={
              <Typography
                className={classes.formSectionTitle}
                variant="h3"
              >{`${objectLabels.quote.plural}/Invoices`}</Typography>
            }
          />
          <div className={classes.formSection}>
            <NotificationOption
              type="quote-accepted"
              showAdmin={showAdmin}
              label={`When an ${objectLabels.quote.value.toLowerCase()} is accepted`}
            />
            <NotificationOption
              type="quote-declined"
              showAdmin={showAdmin}
              label={`When an ${objectLabels.quote.value.toLowerCase()} is declined`}
            />
            {showSalesAdminFeature(featureQuoteViewed) && (
              <NotificationOption
                type="quote-viewed"
                showFrequency
                trigger="self"
                label={`When an ${objectLabels.quote.value.toLowerCase()} is viewed`}
              />
            )}
            {showSalesAdminFeature(featureQuoteViewed) && (
              <NotificationOption
                type="invoice-viewed"
                showFrequency
                trigger="self"
                label={`When an invoice is viewed`}
              />
            )}
            <NotificationOption
              type="quote-additional-work-accepted"
              showAdmin={showAdmin}
              nameOverride="Invoices"
              label={`When additional work is accepted`}
            />
            {!isCrew && (
              <>
                <NotificationOption
                  type="quote-bounced"
                  showAdmin={showAdmin}
                  label={`When an ${objectLabels.quote.value.toLowerCase()} could not be delivered`}
                />
                <NotificationOption
                  type="invoice-bounced"
                  showAdmin={showAdmin}
                  nameOverride="Invoices"
                  label={`When an invoice could not be delivered`} // this one
                />
              </>
            )}
            {!isAdmin && !isViewer && (
              <NotificationOption
                type="quote-assigned"
                showAdmin={showAdmin}
                label={`When an ${objectLabels.quote.value.toLowerCase()} is shared`}
              />
            )}
            <NotificationOption type="payment-received" label={`When a payment is received`} nameOverride="Invoices" />
            <NotificationOption
              type="quote-message"
              showAdmin={showAdmin}
              label={`When a new chat message is received`}
              trigger="self"
            />
          </div>
          {(hasCRM || showAdmin) && (
            <>
              {!isViewer && (
                <>
                  <Divider classes={{ root: classes.divider }} />
                  <FormSectionTitle
                    title={<Typography className={classes.formSectionTitle} variant="h3">{`Deals`}</Typography>}
                  />
                  <div className={classes.formSection}>
                    <NotificationOption type="deal-assigned" label={`When a deal is assigned`} trigger="self" />
                    <NotificationOption
                      type="lead-form-submitted"
                      label={`When a lead form is submitted`}
                      trigger="self"
                    />
                  </div>
                  <Divider classes={{ root: classes.divider }} />
                  <FormSectionTitle
                    title={<Typography className={classes.formSectionTitle} variant="h3">{`Events`}</Typography>}
                  />
                  <div className={classes.formSection}>
                    <NotificationOption type="event-assigned" label={`When an event is assigned`} trigger="self" />
                    <NotificationOption type="event-reassigned" label={`When an event is reassigned`} trigger="self" />
                    <NotificationOption type="event-updated" label={`When an event is updated`} trigger="self" />
                    <NotificationOption type="event-cancelled" label={`When an event is cancelled`} trigger="self" />
                  </div>
                </>
              )}
              <Divider classes={{ root: classes.divider }} />
              <FormSectionTitle
                title={<Typography className={classes.formSectionTitle} variant="h3">{`Tasks`}</Typography>}
              />
              <div className={classes.formSection}>
                <NotificationOption
                  type="task-assigned"
                  label={`When a task is assigned to you by another user`}
                  trigger="self"
                  className={classes.taskOption}
                />

                <NotificationOption
                  type="task-upcoming-weekly"
                  label={`When upcoming tasks are due (weekly reminder)`}
                  nameOverride="Tasks"
                  className={classes.taskOption}
                />
                <NotificationOption
                  type="task-overdue-daily"
                  label={`When tasks are overdue (daily reminder)`}
                  nameOverride="Tasks"
                  className={classes.taskOption}
                />
              </div>
            </>
          )}
        </FormSection>
        {showAdmin && (
          <FormSection className={classes.section}>
            <FormSectionTitle title={`Integration Errors`} variant={'h3'} />
            <NotificationOption
              type="integration-quote-error"
              showAdmin={showAdmin}
              label={`Notification on Integration Quote Errors`}
            />
            <NotificationOption
              nameOverride={'Contacts'}
              type="integration-contact-error"
              showAdmin={showAdmin}
              label={`Notification on Integration Contact Errors`}
            />
            <NotificationOption
              nameOverride={'Authentication'}
              type="integration-authentication-error"
              showAdmin={showAdmin}
              label={`Notification on Integration Authentication Errors`}
            />
          </FormSection>
        )}
        {showSalesAdminFeature(featureStatsDigest) && (
          <FormSection className={classes.section}>
            <FormSectionTitle
              title={`Leaderboards`}
              // subTitle={`Receive weekly summaries of the latest stats including win rate and ${objectLabels.currency.plural.toLowerCase()} won`}
              variant={'h3'}
            />
            <div style={{ marginLeft: 24 }}>
              <NotificationOption
                type="stats-digest-own"
                showAdmin={showAdmin}
                label={`Monthly Recap`}
                trigger="self"
              />
            </div>
          </FormSection>
        )}
      </Form>
    </SettingsPage>
  )
}

interface NotificationOptionProps {
  type: string
  label: string
  nameOverride?: string
  showAdmin?: boolean
  showFrequency?: boolean
  className?: string
  /**
   * Forces the notification to a trigger (all/self)
   *
   * if not defined, then it will show the radio group to let the user select
   */
  trigger?: 'all' | 'self'
}
function NotificationOption(props: NotificationOptionProps) {
  const classes = useStyles(props)
  const { nameOverride } = props
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { isAdmin, isViewer } = useContext(UserContext)
  const { values, setFieldValue } = useFormikContext<ProfileFormikValues>()
  const { preferences } = values
  const showTriggeredBy = (isAdmin || props.showAdmin || isViewer) && !props.trigger

  // get notification from options by type
  const notif =
    values.preferences.notifications &&
    values.preferences.notifications.types &&
    values.preferences.notifications.types[props.type]
      ? values.preferences.notifications.types[props.type]
      : defaultNotificationTypes[props.type]

  interface RadioSelectProps {
    target: 'triggeredBy' | 'frequency'
    label0: string
    label1: string
    val0: string
    val1: string
  }
  const RadioSelect = ({ target, label0, label1, val0, val1 }: RadioSelectProps) => {
    return (
      <RadioGroup
        className={classes.radioGroup}
        value={notif[target]}
        onChange={(ev, value: string) => {
          setFieldValue(`preferences.notifications.types.${props.type}.${target}` as any, value)
          setFieldValue(`preferences.notifications.types.${props.type}.enabled` as any, true)
        }}
      >
        <FormControlLabel
          className={classes.radio}
          label={label0}
          value={val0}
          disabled={!notif.enabled}
          control={<Radio color={'primary'} />}
        />
        <FormControlLabel
          className={classes.radio}
          label={label1}
          value={val1}
          disabled={!notif.enabled}
          control={<Radio color={'primary'} />}
        />
      </RadioGroup>
    )
  }

  return (
    <div className={`${classes.option} ${props.className || ''}`}>
      <FormControlLabel
        className={classes.checkboxLabel}
        control={
          <Checkbox
            checked={!!notif.enabled}
            onChange={(ev, value) => {
              if (
                // if props.trigger is set
                props.trigger ||
                // if preferences.notifications is undefined, default the triggeredBy value
                !preferences.notifications ||
                !preferences.notifications.types ||
                !preferences.notifications.types[props.type]
              ) {
                setFieldValue(
                  `preferences.notifications.types.${props.type}.triggeredBy` as any,
                  props.trigger || 'self'
                )
              }
              if (props.type === 'quote-viewed' && !preferences.notifications?.types?.['quote-viewed']) {
                // need to reset the default for this type's extra field else it is lost when notif switches from defaultNotificationTypes
                setFieldValue(`preferences.notifications.types.quote-viewed.frequency` as any, 'firstView')
              }

              setFieldValue(`preferences.notifications.types.${props.type}.enabled` as any, value)
            }}
          />
        }
        label={props.label}
      />
      {showTriggeredBy && (
        <RadioSelect
          target={'triggeredBy'}
          label0={`My ${nameOverride ? nameOverride : objectLabels.quote.plural}`}
          label1={`All ${nameOverride ? nameOverride : objectLabels.quote.plural}`}
          val0={'self'}
          val1={'all'}
        />
      )}
      {props.showFrequency && (
        <RadioSelect
          target={'frequency'}
          label0={'First View Only'}
          label1={'Each View'}
          val0={'firstView'}
          val1={'eachView'}
        />
      )}
    </div>
  )
}

export default connect<NotificationsFormProps, ProfileFormikValues>(NotificationsForm)
